import { getRouteApi } from '@tanstack/react-router';
import bbox from '@turf/bbox';
import { Layer, Source, useMap } from '@vis.gl/react-maplibre';
import { Fragment, useEffect, useMemo } from 'react';

import { useRoutes } from '@/hooks/useRoutes';
import { BEFORE_LABEL_ID } from '@/modules/BeforeLabelsId';
import { TriasHelper } from '@/modules/TriasHelper';
import { TriasRouteSegments } from '@/modules/TriasRouteSegments';
import { useRoutesStore } from '@/stores/useRoutesStore';
import { useUIStore } from '@/stores/useUIStore';
import { RouteLineType } from '@/types/RouteLineType';
import { RouteLocationType } from '@/types/RouteLocationType';
import type { RouteSegmentsFeatureCollection } from '@/types/RouteSegmentFeature';

const routeApi = getRouteApi('/_map');

const ROUTE = 'ROUTE';
const ROUTE_BORDER = 'ROUTE_BORDER';
export const BEFORE_ROUTE_ID = 'BEFORE_ROUTE';

const SIDEBAR_NAV_WIDTH_EXPANDED = 192;
const SIDEBAR_NAV_WIDTH_COLLAPSED = 68;
const SIDEBAR_CONTENT_WIDTH_APPROX = 400;

export function RouteLayers() {
  const { resultIndex } = routeApi.useSearch();
  const routeLocations = useRoutesStore((state) => state.routeLocations);
  const route = useRoutes();
  const { current: map } = useMap();
  const sidebarExpanded = useUIStore((state) => state.sidebarExpanded);

  const isDetailMode = typeof resultIndex === 'number';
  const isSidebarOpen = typeof resultIndex === 'number';

  useEffect(() => {
    const origin = routeLocations[RouteLocationType.ORIGIN];
    const destination = routeLocations[RouteLocationType.DESTINATION];

    const sidebarNavWidth = sidebarExpanded ? SIDEBAR_NAV_WIDTH_EXPANDED : SIDEBAR_NAV_WIDTH_COLLAPSED;
    const sidebarContentWidth = isSidebarOpen ? SIDEBAR_CONTENT_WIDTH_APPROX : 0;
    const leftPadding = sidebarNavWidth + sidebarContentWidth + 100;

    if (origin && destination && route.data) {
      const segments = TriasRouteSegments.get(route, 0);

      const bounds = bbox({
        type: 'FeatureCollection',
        features: [origin, destination, ...segments.features].filter((value) => !!value),
      });

      map?.fitBounds(bounds as [number, number, number, number], {
        padding: { top: 100, bottom: 50, left: leftPadding, right: 50 },
        animate: true,
        duration: 300,
      });
    } else {
      [origin, destination].forEach((location) => {
        if (location && !map?.getBounds().contains(location.geometry.coordinates as [number, number])) {
          map?.panTo(location.geometry.coordinates as [number, number]);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeLocations, route.data, map, sidebarExpanded, isSidebarOpen]);

  const routeSegments = useMemo(() => {
    if (!route.data) {
      return { type: 'FeatureCollection', features: [] } as RouteSegmentsFeatureCollection;
    }

    if (isDetailMode) {
      return TriasRouteSegments.get(route, resultIndex ?? 0);
    }

    const routeStructures = TriasHelper.getIndividualRouteStructures(route.data);
    const allFeatures: RouteSegmentsFeatureCollection = {
      type: 'FeatureCollection',
      features: [],
    };

    routeStructures?.forEach((_, index) => {
      const segments = TriasRouteSegments.get(route, index);
      allFeatures.features.push(...segments.features);
    });

    return allFeatures;
  }, [route, resultIndex, isDetailMode]);

  return (
    <Fragment>
      <Layer beforeId={BEFORE_LABEL_ID} id={BEFORE_ROUTE_ID} layout={{ visibility: 'none' }} type="background" />

      {route.data ? (
        <Source data={routeSegments} id={ROUTE} type="geojson">
          <Layer
            beforeId={BEFORE_LABEL_ID}
            id={ROUTE_BORDER}
            layout={{ 'line-cap': 'round' }}
            type="line"
            paint={{
              'line-color': 'white',
              'line-width': 12,
            }}
          />

          <Layer
            beforeId={BEFORE_LABEL_ID}
            filter={['==', ['get', 'routeLineType'], RouteLineType.EMERGENCY_SERVICES]}
            id={RouteLineType.EMERGENCY_SERVICES}
            layout={{ 'line-cap': 'round' }}
            type="line"
            paint={{
              'line-color': '#E98A7A',
              'line-width': 6,
              'line-opacity': 0.8,
            }}
          />

          <Layer
            beforeId={BEFORE_LABEL_ID}
            filter={['==', ['get', 'routeLineType'], RouteLineType.STRATEGY_PREFERRED]}
            id={RouteLineType.STRATEGY_PREFERRED}
            layout={{ 'line-cap': 'round' }}
            type="line"
            paint={{
              'line-color': '#F4E3A1',
              'line-width': 6,
              'line-opacity': 0.8,
            }}
          />

          <Layer
            beforeId={BEFORE_LABEL_ID}
            filter={['==', ['get', 'routeLineType'], RouteLineType.STRATEGY_AVOID]}
            id={RouteLineType.STRATEGY_AVOID}
            layout={{ 'line-cap': 'round' }}
            type="line"
            paint={{
              'line-color': '#B9D98A',
              'line-width': 6,
              'line-opacity': 0.8,
            }}
          />
        </Source>
      ) : null}
    </Fragment>
  );
}
